import React, { useMemo, useState } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { Code, Eye } from 'lucide-react';
import DOMPurify from 'dompurify';

const modules = {
  toolbar: [
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
    [{ 'font': [] }],
    [{ 'size': ['small', false, 'large', 'huge'] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'script': 'sub'}, { 'script': 'super' }],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    [{ 'align': [] }],
    ['blockquote', 'code-block'],
    ['link', 'image', 'video'],
    ['clean']
  ]
};

const formats = [
  'header', 'font', 'size',
  'bold', 'italic', 'underline', 'strike',
  'color', 'background',
  'script',
  'list', 'bullet', 'indent',
  'align',
  'blockquote', 'code-block',
  'link', 'image', 'video'
];

export default function RichTextEditor({ value, onChange, placeholder = 'Commencez à écrire...' }) {
  const [showPreview, setShowPreview] = useState(false);
  const [viewMode, setViewMode] = useState('visual'); // 'visual' or 'html'

  const quillModules = useMemo(() => modules, []);
  const quillFormats = useMemo(() => formats, []);

  const handleHtmlChange = (e) => {
    onChange(e.target.value);
  };

  return (
    <div className="rich-text-editor">
      {/* Mode Toggle Buttons */}
      <div className="flex items-center justify-between mb-3 gap-3">
        <div className="flex gap-2 bg-gray-100 p-1 rounded-lg">
          <button
            type="button"
            onClick={() => setViewMode('visual')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
              viewMode === 'visual'
                ? 'bg-white text-[#00aaff] shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Eye size={16} />
            Visuel
          </button>
          <button
            type="button"
            onClick={() => setViewMode('html')}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-colors ${
              viewMode === 'html'
                ? 'bg-white text-[#00aaff] shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Code size={16} />
            HTML
          </button>
        </div>

        <button
          type="button"
          onClick={() => setShowPreview(!showPreview)}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg transition-colors border ${
            showPreview
              ? 'bg-[#00aaff] text-white border-[#00aaff]'
              : 'bg-white text-gray-700 border-gray-200 hover:border-[#00aaff]'
          }`}
        >
          <Eye size={16} />
          {showPreview ? 'Masquer aperçu' : 'Afficher aperçu'}
        </button>
      </div>

      <div className={`grid ${showPreview ? 'grid-cols-2' : 'grid-cols-1'} gap-4`}>
        {/* Editor Column */}
        <div>
          {viewMode === 'visual' ? (
            <ReactQuill
              theme="snow"
              value={value || ''}
              onChange={onChange}
              modules={quillModules}
              formats={quillFormats}
              placeholder={placeholder}
              className="bg-white border-gray-200 rounded-xl rich-text-styled"
              style={{ height: '500px', marginBottom: '60px' }}
            />
          ) : (
            <div className="relative">
              <textarea
                value={value || ''}
                onChange={handleHtmlChange}
                className="w-full h-[500px] p-4 border border-gray-200 rounded-xl font-mono text-sm outline-none focus:border-[#00aaff] resize-none"
                placeholder="<div>Code HTML...</div>"
                style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace' }}
              />
              <div className="absolute top-2 right-2 bg-gray-800 text-white px-2 py-1 rounded text-xs font-mono">
                HTML
              </div>
            </div>
          )}
        </div>

        {/* Preview Column */}
        {showPreview && (
          <div className="border border-gray-200 rounded-xl bg-gray-50 p-6 overflow-y-auto" style={{ height: '560px' }}>
            <div className="bg-white rounded-lg p-6 shadow-sm">
              <div className="mb-3 flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase border-b border-gray-100 pb-2">
                <Eye size={14} />
                Aperçu en temps réel
              </div>
              <div 
                className="prose prose-lg max-w-none blog-content-preview"
                dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(value || '<p class="text-gray-400 italic">L\'aperçu s\'affichera ici...</p>') }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Custom Styles for Rich Content Preview */}
      <style jsx global>{`
        /* Styles pour l'éditeur Quill */
        .rich-text-styled .ql-editor {
          min-height: 400px;
          font-size: 16px;
          line-height: 1.6;
        }

        /* Styles pour l'aperçu du blog */
        .blog-content-preview {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        }

        .blog-content-preview h1,
        .blog-content-preview h2,
        .blog-content-preview h3,
        .blog-content-preview h4 {
          font-weight: 700;
          margin-top: 1.5em;
          margin-bottom: 0.75em;
          line-height: 1.3;
        }

        .blog-content-preview h1 { font-size: 2.25rem; color: #1a202c; }
        .blog-content-preview h2 { font-size: 1.875rem; color: #2d3748; }
        .blog-content-preview h3 { font-size: 1.5rem; color: #2d3748; }
        .blog-content-preview h4 { font-size: 1.25rem; color: #4a5568; }

        .blog-content-preview p {
          margin-bottom: 1em;
          color: #4a5568;
          line-height: 1.7;
        }

        .blog-content-preview ul,
        .blog-content-preview ol {
          margin-left: 1.5em;
          margin-bottom: 1em;
        }

        .blog-content-preview li {
          margin-bottom: 0.5em;
          color: #4a5568;
        }

        .blog-content-preview a {
          color: #00aaff;
          text-decoration: underline;
        }

        .blog-content-preview a:hover {
          color: #0088cc;
        }

        .blog-content-preview blockquote {
          border-left: 4px solid #00aaff;
          padding-left: 1em;
          margin-left: 0;
          font-style: italic;
          color: #718096;
        }

        .blog-content-preview code {
          background-color: #f7fafc;
          padding: 0.2em 0.4em;
          border-radius: 0.25em;
          font-size: 0.875em;
          font-family: monospace;
          color: #e53e3e;
        }

        .blog-content-preview pre {
          background-color: #2d3748;
          color: #f7fafc;
          padding: 1em;
          border-radius: 0.5em;
          overflow-x: auto;
          margin-bottom: 1em;
        }

        .blog-content-preview pre code {
          background: transparent;
          padding: 0;
          color: #f7fafc;
        }

        .blog-content-preview img {
          max-width: 100%;
          height: auto;
          border-radius: 0.5em;
          margin: 1em 0;
        }

        .blog-content-preview table {
          width: 100%;
          border-collapse: collapse;
          margin-bottom: 1em;
        }

        .blog-content-preview table th,
        .blog-content-preview table td {
          border: 1px solid #e2e8f0;
          padding: 0.75em;
          text-align: left;
        }

        .blog-content-preview table th {
          background-color: #f7fafc;
          font-weight: 600;
        }

        /* Styles personnalisés pour les divs colorées (comme dans les articles enrichis) */
        .blog-content-preview .prose {
          max-width: none;
        }

        .blog-content-preview [class*="bg-"],
        .blog-content-preview [class*="border-"],
        .blog-content-preview [class*="rounded-"],
        .blog-content-preview [class*="p-"],
        .blog-content-preview [class*="shadow-"] {
          /* Préserver les classes Tailwind dans l'aperçu */
        }
      `}</style>
    </div>
  );
}
